import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { useWaysToStyle } from "../../context/waysToStyle/WaysToStyleContext";
import WaysToStyleModalThumbnails from "./WaysToStyleModalThumbnails";
import Modal from "../modal/Modal";
import { getDefaultThumbnailIndex } from "./utils";
import {
  MODAL_TYPE,
  WAYS_TO_STYLE_OPEN_MODAL_EVENT,
  MODAL_CONTENT_LOAD_DELAY_MS,
} from "../../constants";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import useEventBus from "../../hooks/useEventBus";
import { StylingLookWithStockPrice } from "../../types/productTypes/stylingLookProduct";
import WaysToStyleModalCarousel from "./WaysToStyleModalCarousel";
import classnamesBind from "classnames/bind";
import WaysToStyleSimilarItems from "./WaysToStyleSimilarItems";
import { reorderThumbnailsBySelectedStylingLook } from "./utils";
import { ProductData } from "../../types/productTypes/product";
import WaysToStyleProductCard from "./WaysToStyleProductCard";
import {
  fireWaysToStyleProductView,
  resetWaysToStyleProductView,
} from "../../analytics/waysToStyle/waysToStyleProductView";
import {
  fireWaysToStyleOutfitView,
  resetWaysToStyleOutfitViewTracking,
} from "../../analytics/waysToStyle/waysToStyleOutfitView";
import styles from "./WaysToStyleModal.scss";

const classnames = classnamesBind.bind(styles);

interface WaysToStyleModalData {
  selectedLook: StylingLookWithStockPrice;
  lookIndex: number;
  thumbnailIndex: number;
}

const WaysToStyleModal: React.FC = () => {
  const { getTranslation, product } = useRuntimeConstants() as {
    getTranslation: (key: string) => string;
    product?: ProductData;
  };
  const { resetStockPriceData, stylingLooksWithStockPrice } = useWaysToStyle();
  const stylingLooksWithStockPriceRef = useRef(stylingLooksWithStockPrice);
  stylingLooksWithStockPriceRef.current = stylingLooksWithStockPrice;
  const [open, setOpen] = useState(false);
  const [contentLoaded, setContentLoaded] = useState(false);
  const [focusOnCloseElement, setFocusOnCloseElement] =
    useState<HTMLElement | null>(null);
  const [selectedStylingLook, setSelectedStylingLook] =
    useState<StylingLookWithStockPrice | null>(null);
  const selectedStylingLookRef = useRef(selectedStylingLook);
  selectedStylingLookRef.current = selectedStylingLook;
  const [modalId] = useState<string>(
    `${MODAL_TYPE.SIDE}${Math.floor(Math.random() * 10000)}`
  );
  const [selectedThumbnailIndex, setSelectedThumbnailIndex] = useState(1);
  const contentLoadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [modalHeaderHeight, setModalHeaderHeight] = useState(0);

  const orderedThumbnails = useMemo(() => {
    if (!selectedStylingLook) return [];
    return reorderThumbnailsBySelectedStylingLook(
      selectedStylingLook.products,
      product?.id
    );
  }, [selectedStylingLook, product?.id]);

  const selectedThumbnailProduct =
    orderedThumbnails[selectedThumbnailIndex] ?? orderedThumbnails[0];

  const clearContentLoadTimer = () => {
    if (contentLoadTimer.current) {
      clearTimeout(contentLoadTimer.current);
      contentLoadTimer.current = null;
    }
  };

  useEffect(() => clearContentLoadTimer, []);

  useEffect(() => {
    if (!open || !selectedThumbnailProduct) {
      return;
    }

    fireWaysToStyleProductView({
      productId: selectedThumbnailProduct.id,
      index: orderedThumbnails.indexOf(selectedThumbnailProduct),
    });
  }, [open, selectedThumbnailProduct, orderedThumbnails]);

  const handleLookIndexChange = useCallback((newLookIndex: number) => {
    const currentLook = stylingLooksWithStockPriceRef.current[newLookIndex];

    if (currentLook?.id === selectedStylingLookRef.current?.id) {
      return;
    }

    setSelectedStylingLook(currentLook);
    const defaultThumbnailIndex = getDefaultThumbnailIndex(
      currentLook?.products
    );
    setSelectedThumbnailIndex(defaultThumbnailIndex);

    if (currentLook) {
      fireWaysToStyleOutfitView({
        products: currentLook.products,
        lookIndex: newLookIndex,
      });
    }
  }, []);

  useEventBus(
    WAYS_TO_STYLE_OPEN_MODAL_EVENT,
    ({
      focusOnCloseElement,
      data,
    }: {
      focusOnCloseElement: HTMLElement;
      data?: WaysToStyleModalData | null;
    }) => {
      setOpen(true);
      setFocusOnCloseElement(focusOnCloseElement);

      if (!data) {
        clearContentLoadTimer();
        setContentLoaded(false);
        setSelectedStylingLook(null);
        return;
      }
      setSelectedStylingLook(data.selectedLook);
      setSelectedThumbnailIndex(data.thumbnailIndex);

      fireWaysToStyleOutfitView({
        products: data.selectedLook.products,
        lookIndex: data.lookIndex,
      });

      clearContentLoadTimer();
      contentLoadTimer.current = setTimeout(() => {
        setContentLoaded(true);
      }, MODAL_CONTENT_LOAD_DELAY_MS);
    },
    []
  );

  const renderContent = () => {
    if (!selectedStylingLook) return null;

    return (
      <div className={classnames({ isLoading: !contentLoaded })}>
        <WaysToStyleModalCarousel
          selectedStylingLook={selectedStylingLook}
          onSelectedLookIndexChange={handleLookIndexChange}
          isOpen={open}
        />
        <div
          className={classnames(styles.thumbnailContainer, styles.sticky)}
          style={{ top: modalHeaderHeight }}
          data-testid="modalThumbnailContainer"
        >
          <WaysToStyleModalThumbnails
            products={orderedThumbnails}
            selectedThumbnailIndex={selectedThumbnailIndex}
            onThumbnailSelect={setSelectedThumbnailIndex}
          />
        </div>
        {selectedThumbnailProduct ? (
          <>
            <div className={styles.productCardContainer}>
              <WaysToStyleProductCard
                product={selectedThumbnailProduct}
                looksProducts={selectedStylingLook.products}
              />
            </div>
            <WaysToStyleSimilarItems
              selectedProductId={selectedThumbnailProduct?.id}
            />
          </>
        ) : null}
      </div>
    );
  };

  return (
    <Modal
      title={getTranslation("pdp_associated_buy_the_look_title")}
      open={open}
      focusOnClose={focusOnCloseElement}
      modalId={modalId}
      modalType={MODAL_TYPE.SIDE}
      forceDOMRewrite={true}
      closeModal={() => {
        clearContentLoadTimer();
        setOpen(false);
        setContentLoaded(false);
        resetStockPriceData();
        resetWaysToStyleProductView();
        resetWaysToStyleOutfitViewTracking();
      }}
      contentLoaded={contentLoaded}
      additionalClasses={styles.uppercaseTitle}
      onHeaderHeightChange={setModalHeaderHeight}
    >
      {renderContent()}
    </Modal>
  );
};

export default WaysToStyleModal;
